import { definePreset, palette, type ColorScale } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const keolisPrimary = palette('#008296');
const keolisSecondary = palette('#776E65');
const keolisSurfaceLight = palette('#64748b') as ColorScale;
const keolisSurfaceDark = palette('#1A1A1A') as ColorScale;

const KeolisAuraPreset = definePreset(Aura, {
  primitive: {
    keolisPrimary,
    keolisSecondary,
  },
  semantic: {
    primary: keolisPrimary,
    colorScheme: {
      light: {
        surface: {
          0: '#FFFFFF',
          ...keolisSurfaceLight,
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#FFFFFF',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        secondary: {
          color: '{keolisSecondary.500}',
          contrastColor: '#FFFFFF',
          hoverColor: '{keolisSecondary.600}',
          activeColor: '{keolisSecondary.700}',
        },
      },
      dark: {
        surface: {
          0: '#050505',
          ...keolisSurfaceDark,
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '#1A1A1A',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        secondary: {
          color: '{keolisSecondary.300}',
          contrastColor: '#1A1A1A',
          hoverColor: '{keolisSecondary.200}',
          activeColor: '{keolisSecondary.100}',
        },
      },
    },
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.300}',
      offset: '2px',
    },
  },
  components: {
    selectbutton: {
      root: {
        borderRadius: '1rem',
      },
    },
    togglebutton: {
      root: {
        borderRadius: '0.75rem',
        fontWeight: '700',
      },
      content: {
        borderRadius: '0.75rem',
        sm: {
          padding: '0.625rem 0.75rem',
        },
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            checkedBackground: '{primary.600}',
            hoverBackground: '{primary.50}',
            borderColor: '{surface.300}',
            color: '{surface.700}',
            hoverColor: '{primary.700}',
            checkedColor: '#FFFFFF',
            checkedBorderColor: '{primary.600}',
          },
          content: {
            checkedBackground: '{primary.600}',
          },
        },
        dark: {
          root: {
            background: '{surface.900}',
            checkedBackground: '{primary.400}',
            hoverBackground: '{primary.950}',
            borderColor: '{surface.700}',
            color: '{surface.200}',
            hoverColor: '{primary.200}',
            checkedColor: '#1A1A1A',
            checkedBorderColor: '{primary.400}',
          },
          content: {
            checkedBackground: '{primary.400}',
          },
        },
      },
    },
    datatable: {
      colorScheme: {
        light: {
          headerCell: {
            background: '{surface.50}',
            hoverBackground: '{primary.50}',
            selectedBackground: '{primary.100}',
            hoverColor: '{primary.700}',
          },
          row: {
            hoverBackground: '{primary.100}',
            hoverColor: '{primary.700}',
          },
          sortIcon: {
            hoverColor: '{primary.700}',
          },
        },
        dark: {
          headerCell: {
            background: '{surface.800}',
            hoverBackground: '{primary.950}',
            selectedBackground: '{primary.900}',
            color: '{surface.200}',
            hoverColor: '{primary.200}',
            selectedColor: '{primary.100}',
          },
          row: {
            hoverBackground: '{primary.900}',
            hoverColor: '{primary.100}',
          },
          sortIcon: {
            hoverColor: '{primary.200}',
          },
        },
      },
    },
    progressspinner: {
      colorScheme: {
        light: {
          root: {
            colorOne: '{primary.color}',
            colorTwo: '{primary.color}',
            colorThree: '{primary.color}',
            colorFour: '{primary.color}',
          },
        },
        dark: {
          root: {
            colorOne: '{primary.color}',
            colorTwo: '{primary.color}',
            colorThree: '{primary.color}',
            colorFour: '{primary.color}',
          },
        },
      },
    },
    progressbar: {
      root: {
        height: '0.75rem',
      },
    },
    card: {
      root: {
        borderRadius: '2rem',
        shadow: '0 8px 24px 0 rgba(0, 31, 39, 0.06)',
      },
    },
    toast: {
      root: {
        borderRadius: '0.75rem',
        borderWidth: '1px',
        blur: '8px',
      },
      colorScheme: {
        light: {
          info: {
            background: 'rgba(224, 244, 246, 0.94)',
            borderColor: '{primary.300}',
            color: '{surface.900}',
            detailColor: '{surface.700}',
            shadow: '0 0.75rem 1.75rem rgba(0, 72, 82, 0.16)',
          },
          success: {
            background: 'rgba(224, 246, 236, 0.96)',
            borderColor: '{green.300}',
            color: '{surface.900}',
            detailColor: '{surface.700}',
            shadow: '0 0.75rem 1.75rem rgba(22, 132, 91, 0.15)',
          },
          warn: {
            background: 'rgba(255, 244, 216, 0.97)',
            borderColor: '{yellow.400}',
            color: '{surface.900}',
            detailColor: '{surface.700}',
            shadow: '0 0.75rem 1.75rem rgba(154, 101, 0, 0.15)',
          },
          error: {
            background: 'rgba(255, 230, 233, 0.97)',
            borderColor: '{red.300}',
            color: '{surface.900}',
            detailColor: '{surface.700}',
            shadow: '0 0.75rem 1.75rem rgba(189, 63, 73, 0.16)',
          },
        },
      },
    },
    listbox: {
      option: {
        focusBackground: '{primary.100}',
        selectedBackground: '{primary.200}',
        selectedFocusBackground: '{primary.300}',
        focusColor: '{primary.700}',
        selectedColor: '{primary.700}',
        selectedFocusColor: '{primary.700}',
      },
    },
  },
});

export default KeolisAuraPreset;
